import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { filterRequest } from "../../utilities/apiManager";
import axiosInstance from "../../utilities/axiosInstance";

let initialState = {
  isLoading: false,
  data: [],
  property: null,
  itemsCount: 0,
  pages: 0,
  error: null,
}

export const getProperties = createAsyncThunk(
  "properties/getProperties",
  async (args, thunkApi) => {
    try {
      const { page = 1, size = 10, query, promoted, owner, ...otherArgs } = args;

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', size);

      // Only add optional params if they have values
      if (owner) params.append('owner', owner);
      if (promoted) params.append('promoted', promoted);
      if (query) params.append('search', query);

      const filter = filterRequest(otherArgs.filter);

      const { data } = await axiosInstance.get(
        `/properties?${params.toString()}${filter}`
      );
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getPropertyById = createAsyncThunk(
  "properties/getPropertyById",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(
        `/properties/${args.propertyId}`
      );
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const updatePropertyStatus = createAsyncThunk(
  "properties/updatePropertyStatus",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.put(
        `/properties/${args._id}/status`,
        args.values
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const deleteProperty = createAsyncThunk(
  "properties/deleteProperty",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.delete(
        `/properties/${args._id}`
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

const propertiesSlice = createSlice({
  name: "properties",
  initialState,
  reducers: {
    resetProperties: (state) => {
      state.data = [];
      state.itemsCount = 0;
      state.pages = 0;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null
      })
      .addCase(getProperties.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.data = payload.data?.data;
        state.itemsCount = payload.data?.itemsCount;
        state.pages = payload.data?.pages;
      })
      .addCase(getProperties.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(getPropertyById.pending, (state) => {
        state.isLoading = true;
        state.error = null
      })
      .addCase(getPropertyById.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.property = payload.data;
      })
      .addCase(getPropertyById.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(updatePropertyStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePropertyStatus.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const indexAt = state.data.findIndex(el => el._id === payload.data._id);
        state.data[indexAt] = payload.data;
        state.error = null;
      })
      .addCase(updatePropertyStatus.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(deleteProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.data = state.data.filter(el => el._id !== payload.data._id);
        state.error = null;
      })
      .addCase(deleteProperty.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })
  }
});

export const { resetProperties } = propertiesSlice.actions;
export default propertiesSlice.reducer;